import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import {
  LogOut, CalendarDays, Banknote, ListChecks, UserPlus, Loader2,
  Plus, Trash2, RefreshCw, Check as CheckIcon, X as XIcon, Ban,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAdminAuth } from "@/admin/AdminAuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prenota-diretto/artifacts/qjerng52_logo%20mm.png";
const MONTH_NAMES = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const TABS = [
  { id: "calendar",  label: "Calendario",  icon: CalendarDays },
  { id: "pricing",   label: "Prezzi",       icon: Banknote },
  { id: "bookings",  label: "Prenotazioni", icon: ListChecks },
  { id: "manual",    label: "Aggiungi",     icon: UserPlus },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, logout, apiAuth, token } = useAdminAuth();
  const [tab, setTab] = useState("calendar");

  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [token, navigate]);

  const onLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="bg-white border-b border-cream-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-cream-100 grid place-items-center">
              <img src={LOGO_URL} alt="" className="w-6 h-6 object-contain" />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-navy-700 font-semibold">Owner area</p>
              <p className="text-sm font-bold text-slate-900 leading-tight">Attico Skyline Milano</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <p className="hidden sm:block text-xs text-slate-600">{admin?.email}</p>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-navy-700">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="bg-white border-b border-cream-200 sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-1 overflow-x-auto scrollbar-hide">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`inline-flex items-center gap-2 px-4 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === tb.id ? "border-navy-700 text-navy-700" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <tb.icon size={15} />
              {tb.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {tab === "calendar" && <CalendarTab apiAuth={apiAuth} />}
        {tab === "pricing" && <PricingTab apiAuth={apiAuth} />}
        {tab === "bookings" && <BookingsTab apiAuth={apiAuth} />}
        {tab === "manual" && <ManualReservationTab apiAuth={apiAuth} />}
      </main>
    </div>
  );
}

/* ============== CALENDAR TAB ============== */
function CalendarTab({ apiAuth }) {
  const [data, setData] = useState({ blocked_dates: [], manual_blocks: [], bookings: [] });
  const [range, setRange] = useState(undefined);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiAuth().get("/admin/calendar");
      setData(data);
    } catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, [apiAuth]);

  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [refresh]);

  const blockedSet = useMemo(() => new Set(data.blocked_dates), [data.blocked_dates]);
  const activeBookings = useMemo(() => {
    const ACTIVE = new Set(["pending", "richiesta_inviata", "confirmed", "confermata"]);
    return (data.bookings || []).filter((b) => ACTIVE.has((b.status || "").toLowerCase()));
  }, [data.bookings]);
  const bookingDates = useMemo(() => {
    const set = new Set();
    activeBookings.forEach((b) => {
      const d1 = parseISO(b.check_in);
      const d2 = parseISO(b.check_out);
      const cur = new Date(d1);
      while (cur < d2) { set.add(format(cur, "yyyy-MM-dd")); cur.setDate(cur.getDate() + 1); }
    });
    return set;
  }, [activeBookings]);
  const manualBlockDates = useMemo(() => {
    const set = new Set();
    (data.manual_blocks || []).forEach((b) => {
      const d1 = parseISO(b.start_date);
      const d2 = parseISO(b.end_date);
      const cur = new Date(d1);
      while (cur < d2) { set.add(format(cur, "yyyy-MM-dd")); cur.setDate(cur.getDate() + 1); }
    });
    return set;
  }, [data.manual_blocks]);

  const applyBlock = async () => {
    if (!range?.from || !range?.to) return toast.error("Seleziona un intervallo");
    try {
      await apiAuth().post("/admin/calendar/block", {
        start_date: format(range.from, "yyyy-MM-dd"),
        end_date: format(new Date(range.to.getTime() + 86400000), "yyyy-MM-dd"),
        reason: reason || "Bloccato manualmente",
      });
      toast.success("Date bloccate");
      setRange(undefined);
      setReason("");
      refresh();
    } catch { toast.error("Errore"); }
  };

  const applyUnblock = async () => {
    if (!range?.from) return toast.error("Seleziona una data o un intervallo");
    const rangeStart = range.from;
    // Accept single-date selection (range.to may be undefined)
    const endBase = range.to || range.from;
    const rangeEnd = new Date(endBase.getTime() + 86400000); // inclusive end
    const overlapping = (data.manual_blocks || []).filter((b) => {
      const bs = parseISO(b.start_date);
      const be = parseISO(b.end_date);
      return bs < rangeEnd && be > rangeStart;
    });
    if (overlapping.length === 0) {
      return toast.error("Nessun blocco manuale in queste date. Le prenotazioni confermate si sbloccano solo dalla tab Prenotazioni.");
    }
    try {
      await Promise.all(overlapping.map((b) => apiAuth().delete(`/admin/calendar/block/${b.id}`)));
      toast.success(`${overlapping.length} blocco/i rimosso/i`);
      setRange(undefined);
      setReason("");
      refresh();
    } catch { toast.error("Errore"); }
  };

  const removeBlock = async (id) => {
    try {
      await apiAuth().delete(`/admin/calendar/block/${id}`);
      toast.success("Blocco rimosso");
      refresh();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7">
        <SectionTitle title="Calendario" subtitle="Le date occupate non sono prenotabili dai visitatori." onRefresh={refresh} />
        <div className="bg-white border border-cream-200 rounded-2xl p-5">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            locale={it}
            modifiers={{
              booking: (d) => bookingDates.has(format(d, "yyyy-MM-dd")),
              manual: (d) => manualBlockDates.has(format(d, "yyyy-MM-dd")),
            }}
            modifiersClassNames={{
              booking: "bg-navy-100 text-navy-800 font-semibold rounded-full",
              manual:  "bg-red-100 text-red-700 font-semibold rounded-full",
            }}
          />
          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-slate-600">
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-navy-100 border border-navy-200" /> Prenotazione</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-100 border border-red-200" /> Blocco manuale</span>
          </div>
        </div>

        <div className="bg-white border border-cream-200 rounded-2xl p-5 mt-6">
          <p className="text-sm font-semibold text-slate-900 mb-3">Blocca o sblocca intervallo selezionato</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input placeholder="Motivo (opzionale, solo per il blocco)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button data-testid="admin-block-apply" onClick={applyBlock} disabled={!range?.from || !range?.to} className="inline-flex items-center gap-2 rounded-full bg-navy-700 hover:bg-navy-800 disabled:opacity-50 text-white px-5 py-2.5 text-sm font-semibold whitespace-nowrap">
              <Plus size={14} /> Blocca date
            </button>
            <button data-testid="admin-unblock-apply" onClick={applyUnblock} disabled={!range?.from || !range?.to} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-300 hover:border-navy-700 hover:text-navy-700 disabled:opacity-50 text-slate-700 px-5 py-2.5 text-sm font-semibold whitespace-nowrap">
              <Trash2 size={14} /> Sblocca date
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            {range?.from ? `${format(range.from, "d MMM yyyy", { locale: it })}${range.to ? ` → ${format(range.to, "d MMM yyyy", { locale: it })}` : ""}` : "Seleziona una data o un intervallo sul calendario qui sopra."}
          </p>
          <p className="text-[11px] text-slate-400 mt-2">Le date delle prenotazioni confermate non possono essere sbloccate da qui — usa la tab Prenotazioni e clicca su &laquo;Cancel&raquo;.</p>
        </div>
      </div>

      <div className="lg:col-span-5">
        <SectionTitle title="Blocchi manuali" subtitle={`${data.manual_blocks?.length || 0} in totale`} />
        <div className="space-y-3">
          {(data.manual_blocks || []).length === 0 && (
            <div className="bg-white border border-cream-200 rounded-2xl p-5 text-sm text-slate-500">Nessun blocco manuale attivo.</div>
          )}
          {(data.manual_blocks || []).map((b) => (
            <div key={b.id} className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{b.start_date} → {b.end_date}</p>
                <p className="text-xs text-slate-600">{b.reason}</p>
              </div>
              <button data-testid={`admin-unblock-${b.id}`} onClick={() => removeBlock(b.id)} className="text-slate-500 hover:text-red-600 p-2" aria-label="Rimuovi blocco">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        {loading && <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Aggiornamento...</p>}
      </div>
    </div>
  );
}

/* ============== PRICING TAB ============== */
function PricingTab({ apiAuth }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newOverride, setNewOverride] = useState({ start: "", end: "", price: "", label: "" });
  const [newDay, setNewDay] = useState({ date: "", price: "" });

  const refresh = useCallback(async () => {
    const { data } = await apiAuth().get("/admin/pricing");
    setCfg(data);
  }, [apiAuth]);

  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [refresh]);

  if (!cfg) return <Loader2 className="animate-spin text-navy-600" />;

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await apiAuth().put("/admin/pricing", patch);
      setCfg(data);
      toast.success("Prezzi aggiornati");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const updateBase = (val) => save({ base_price: parseFloat(val) || 0 });
  const updateMonth = (m, val) => save({ monthly: { ...cfg.monthly, [m]: parseFloat(val) || 0 } });

  const addRangeOverride = () => {
    if (!newOverride.start || !newOverride.end || !newOverride.price) return toast.error("Compila tutti i campi");
    const list = [...(cfg.date_overrides || []), {
      id: crypto.randomUUID(),
      start: newOverride.start,
      end: newOverride.end,
      price: parseFloat(newOverride.price),
      label: newOverride.label || "Periodo speciale",
    }];
    save({ date_overrides: list });
    setNewOverride({ start: "", end: "", price: "", label: "" });
  };

  const removeRange = (id) => save({ date_overrides: (cfg.date_overrides || []).filter((o) => o.id !== id) });

  const addDay = () => {
    if (!newDay.date || !newDay.price) return toast.error("Compila tutti i campi");
    save({ day_overrides: { ...(cfg.day_overrides || {}), [newDay.date]: parseFloat(newDay.price) } });
    setNewDay({ date: "", price: "" });
  };
  const removeDay = (date) => {
    const next = { ...(cfg.day_overrides || {}) };
    delete next[date];
    save({ day_overrides: next });
  };

  return (
    <div className="space-y-8">
      <div>
        <SectionTitle title="Prezzo base" subtitle="Usato quando non c'è nessun override applicabile." />
        <div className="bg-white border border-cream-200 rounded-2xl p-5 flex items-center gap-3 max-w-md">
          <span className="text-sm text-slate-600">€</span>
          <Input data-testid="admin-base-price" type="number" defaultValue={cfg.base_price} onBlur={(e) => updateBase(e.target.value)} />
          <span className="text-xs text-slate-500 whitespace-nowrap">/ notte</span>
        </div>
      </div>

      <div>
        <SectionTitle title="Prezzi mensili" subtitle="Si applicano se non c'è un override di periodo o di giorno." />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {MONTH_NAMES.map((name, i) => (
            <div key={i} className="bg-white border border-cream-200 rounded-2xl p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold mb-2">{name}</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">€</span>
                <Input
                  data-testid={`admin-month-${i + 1}`}
                  type="number"
                  defaultValue={cfg.monthly[String(i + 1)]}
                  onBlur={(e) => updateMonth(String(i + 1), e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle title="Override per intervallo" subtitle="Es. ponti, festività, eventi." />
        <div className="bg-white border border-cream-200 rounded-2xl p-5 grid md:grid-cols-5 gap-3 mb-3">
          <Input type="date" value={newOverride.start} onChange={(e) => setNewOverride({ ...newOverride, start: e.target.value })} />
          <Input type="date" value={newOverride.end} onChange={(e) => setNewOverride({ ...newOverride, end: e.target.value })} />
          <Input type="number" placeholder="Prezzo €" value={newOverride.price} onChange={(e) => setNewOverride({ ...newOverride, price: e.target.value })} />
          <Input placeholder="Etichetta (es. Natale)" value={newOverride.label} onChange={(e) => setNewOverride({ ...newOverride, label: e.target.value })} />
          <button data-testid="admin-add-range" onClick={addRangeOverride} className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-700 hover:bg-navy-800 text-white text-sm font-semibold px-4 py-2.5"><Plus size={14} /> Aggiungi</button>
        </div>
        <div className="space-y-2">
          {(cfg.date_overrides || []).length === 0 && <p className="text-sm text-slate-500">Nessun override attivo.</p>}
          {(cfg.date_overrides || []).map((o) => (
            <div key={o.id} className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{o.label || "Periodo"}</p>
                <p className="text-xs text-slate-600">{o.start} → {o.end} · € {o.price}/notte</p>
              </div>
              <button onClick={() => removeRange(o.id)} className="text-slate-500 hover:text-red-600 p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle title="Override singolo giorno" subtitle="Es. weekend con prezzo specifico." />
        <div className="bg-white border border-cream-200 rounded-2xl p-5 grid md:grid-cols-3 gap-3 mb-3">
          <Input type="date" value={newDay.date} onChange={(e) => setNewDay({ ...newDay, date: e.target.value })} />
          <Input type="number" placeholder="Prezzo €" value={newDay.price} onChange={(e) => setNewDay({ ...newDay, price: e.target.value })} />
          <button data-testid="admin-add-day" onClick={addDay} className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-700 hover:bg-navy-800 text-white text-sm font-semibold px-4 py-2.5"><Plus size={14} /> Aggiungi</button>
        </div>
        <div className="space-y-2">
          {Object.keys(cfg.day_overrides || {}).length === 0 && <p className="text-sm text-slate-500">Nessun override singolo.</p>}
          {Object.entries(cfg.day_overrides || {}).sort().map(([d, p]) => (
            <div key={d} className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center justify-between">
              <p className="text-sm text-slate-900">{d} · <strong>€ {p}</strong>/notte</p>
              <button onClick={() => removeDay(d)} className="text-slate-500 hover:text-red-600 p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      {saving && <p className="text-xs text-navy-600 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Salvataggio...</p>}
    </div>
  );
}

/* ============== BOOKINGS TAB ============== */
const STATUS_LABEL = {
  pending: "Pending", richiesta_inviata: "Pending",
  confirmed: "Confirmed", confermata: "Confirmed",
  rejected: "Rejected", cancelled: "Cancelled",
};
const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-700",
  rejected: "bg-slate-200 text-slate-600 line-through-cell",
  cancelled: "bg-red-100 text-red-700",
};
const normStatus = (s) => {
  if (s === "richiesta_inviata") return "pending";
  if (s === "confermata") return "confirmed";
  return s || "pending";
};

function BookingsTab({ apiAuth }) {
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState({ count: 0, bookings: [] });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // {id, action, label}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiAuth().get(`/admin/bookings?filter=${filter}`);
      setData(data);
    } finally { setLoading(false); }
  }, [apiAuth, filter]);

  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [refresh]);

  const performAction = async (id, action) => {
    setBusyId(id);
    try {
      await apiAuth().post(`/admin/bookings/${id}/${action}`);
      toast.success(action === "accept" ? "Prenotazione confermata" : action === "reject" ? "Prenotazione rifiutata" : "Prenotazione cancellata");
      setConfirmDialog(null);
      refresh();
    } catch { toast.error("Errore"); }
    finally { setBusyId(null); }
  };

  const askConfirm = (id, action) => {
    const labels = {
      reject: { title: "Rifiutare la prenotazione?", text: "Le date torneranno disponibili sul sito pubblico. La richiesta resterà visibile come 'Rejected'." },
      cancel: { title: "Cancellare la prenotazione?", text: "Le date torneranno disponibili sul sito pubblico. La prenotazione resterà visibile come 'Cancelled'." },
    };
    setConfirmDialog({ id, action, ...labels[action] });
  };

  const filters = [
    { id: "pending",   label: "Pending" },
    { id: "confirmed", label: "Confirmed" },
    { id: "rejected",  label: "Rejected" },
    { id: "cancelled", label: "Cancelled" },
    { id: "upcoming",  label: "Future" },
    { id: "past",      label: "Passate" },
    { id: "manual",    label: "Manuali" },
    { id: "all",       label: "Tutte" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <SectionTitle title={`Prenotazioni (${data.count})`} subtitle="Accetta, rifiuta o cancella richieste. Le date si aggiornano in tempo reale sul sito." />
        <div className="flex items-center gap-1 bg-white border border-cream-200 rounded-full p-1 overflow-x-auto scrollbar-hide max-w-full">
          {filters.map((f) => (
            <button
              key={f.id}
              data-testid={`admin-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                filter === f.id ? "bg-navy-700 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-cream-50 border-b border-cream-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 font-semibold">Ospite</th>
                <th className="px-4 py-3 font-semibold">Check-in</th>
                <th className="px-4 py-3 font-semibold">Check-out</th>
                <th className="px-4 py-3 font-semibold">Notti</th>
                <th className="px-4 py-3 font-semibold">Ospiti</th>
                <th className="px-4 py-3 font-semibold">Totale</th>
                <th className="px-4 py-3 font-semibold">Stato</th>
                <th className="px-4 py-3 font-semibold">Origine</th>
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {data.bookings.map((b) => {
                const totalGuests = (b.guests?.adulti || 0) + (b.guests?.bambini || 0) + (b.guests?.neonati || 0);
                const status = normStatus(b.status);
                const canAccept = status === "pending";
                const canReject = status === "pending";
                const canCancel = status === "confirmed";
                return (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} className="hover:bg-cream-50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{b.nome} {b.cognome}</p>
                      <p className="text-xs text-slate-500">{b.email}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{b.check_in}</td>
                    <td className="px-4 py-3 text-slate-700">{b.check_out}</td>
                    <td className="px-4 py-3 text-slate-700">{b.nights}</td>
                    <td className="px-4 py-3 text-slate-700">{totalGuests}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">€ {Number(b.total_price).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span data-testid={`booking-status-${b.id}`} className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[status] || "bg-cream-200 text-slate-700"}`}>
                        {STATUS_LABEL[status] || status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{b.source || "diretto"}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{b.id.slice(0, 8).toUpperCase()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {canAccept && (
                          <button
                            data-testid={`accept-${b.id}`}
                            disabled={busyId === b.id}
                            onClick={() => performAction(b.id, "accept")}
                            className="inline-flex items-center gap-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-[11px] font-semibold px-3 py-1.5 disabled:opacity-50"
                            title="Accept"
                          >
                            <CheckIcon size={12} /> Accept
                          </button>
                        )}
                        {canReject && (
                          <button
                            data-testid={`reject-${b.id}`}
                            disabled={busyId === b.id}
                            onClick={() => askConfirm(b.id, "reject")}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 text-[11px] font-semibold px-3 py-1.5 disabled:opacity-50"
                            title="Reject"
                          >
                            <XIcon size={12} /> Reject
                          </button>
                        )}
                        {canCancel && (
                          <button
                            data-testid={`cancel-${b.id}`}
                            disabled={busyId === b.id}
                            onClick={() => askConfirm(b.id, "cancel")}
                            className="inline-flex items-center gap-1 rounded-full bg-red-100 hover:bg-red-200 text-red-700 text-[11px] font-semibold px-3 py-1.5 disabled:opacity-50"
                            title="Cancel"
                          >
                            <Ban size={12} /> Cancel
                          </button>
                        )}
                        {!canAccept && !canReject && !canCancel && <span className="text-[11px] text-slate-400">—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data.bookings.length === 0 && !loading && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500 text-sm">Nessuna prenotazione in questa vista.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmDialog && (
        <div data-testid="confirm-dialog" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setConfirmDialog(null)}>
          <div className="bg-white rounded-2xl p-7 max-w-sm w-full shadow-elevate" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold text-slate-900 mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">{confirmDialog.text}</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmDialog(null)} className="text-sm font-medium text-slate-600 hover:text-slate-900 px-4 py-2">Annulla</button>
              <button
                data-testid="confirm-dialog-confirm"
                onClick={() => performAction(confirmDialog.id, confirmDialog.action)}
                disabled={busyId === confirmDialog.id}
                className={`inline-flex items-center gap-2 rounded-full text-white text-sm font-semibold px-5 py-2 disabled:opacity-60 ${
                  confirmDialog.action === "reject" ? "bg-slate-700 hover:bg-slate-800" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {busyId === confirmDialog.id ? <Loader2 size={14} className="animate-spin" /> : null}
                Confermo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============== MANUAL RESERVATION TAB ============== */
function ManualReservationTab({ apiAuth }) {
  const [form, setForm] = useState({
    guest_name: "", guest_email: "", telefono: "",
    check_in: "", check_out: "", adulti: 2, bambini: 0, note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        adulti: Number(form.adulti) || 1,
        bambini: Number(form.bambini) || 0,
      };
      await apiAuth().post("/admin/bookings/manual", payload);
      toast.success("Prenotazione manuale creata");
      setForm({ guest_name: "", guest_email: "", telefono: "", check_in: "", check_out: "", adulti: 2, bambini: 0, note: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl">
      <SectionTitle title="Aggiungi prenotazione manuale" subtitle="Le date selezionate verranno bloccate automaticamente." />
      <form onSubmit={submit} className="bg-white border border-cream-200 rounded-2xl p-6 md:p-8 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Nome ospite">
            <Input data-testid="admin-manual-name" required value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Marco Rossi" />
          </Field>
          <Field label="Email">
            <Input data-testid="admin-manual-email" type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} placeholder="opzionale" />
          </Field>
          <Field label="Telefono">
            <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="opzionale" />
          </Field>
          <Field label="Adulti / Bambini">
            <div className="flex gap-2">
              <Input type="number" min={1} value={form.adulti} onChange={(e) => setForm({ ...form, adulti: e.target.value })} />
              <Input type="number" min={0} value={form.bambini} onChange={(e) => setForm({ ...form, bambini: e.target.value })} />
            </div>
          </Field>
          <Field label="Check-in">
            <Input data-testid="admin-manual-checkin" type="date" required value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
          </Field>
          <Field label="Check-out">
            <Input data-testid="admin-manual-checkout" type="date" required value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
          </Field>
        </div>
        <Field label="Note">
          <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} placeholder="Note interne (opzionale)" />
        </Field>
        <button data-testid="admin-manual-submit" type="submit" disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-navy-700 hover:bg-navy-800 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crea prenotazione
        </button>
      </form>
    </div>
  );
}

/* ============== Helpers ============== */
function SectionTitle({ title, subtitle, onRefresh }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
      </div>
      {onRefresh && (
        <button onClick={onRefresh} className="text-xs text-slate-500 hover:text-navy-700 inline-flex items-center gap-1"><RefreshCw size={12} /> Aggiorna</button>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold mb-2 block">{label}</span>
      {children}
    </label>
  );
}
